import { lazy, Suspense, useEffect, useState } from "react";

import { api, imageUrl } from "../api.js";
import { CategoryIcon } from "../lib/icons.jsx";
import { pickName, useT } from "../lib/i18n.jsx";
import { lowPower } from "../lib/lowPower.js";

// Only a POI that has a clip fetches the player (item 0323).
const PoiVideo = lazy(() => import("./PoiVideo.jsx"));

/** Floating dark panel (Situm style): home = search + category grid;
 *  category/search = POI list; poi = detail card with "show route". The view
 *  stack lives in ViewerPage so the map and the panel stay in sync.
 *
 *  `compact` is a phone-shaped viewport (item 0093). There the panel is a
 *  bottom sheet the visitor can dismiss, because 340px x 70vh of a 390px phone
 *  is the whole screen and the map -- the thing they came for -- was a sliver
 *  behind it. Collapsed, it is the venue's own logo: a target they recognise,
 *  and one that needs no new field because the panel header already renders it.
 *  Above phone width it never collapses (item 0314) -- `collapsible` is only
 *  ever true on a phone; nobody taps a logo on a wall to find out a menu exists.
 *
 *  `stacked` (item 0314): a route is active in the 641-819px band, where the
 *  card and the directions panel do not fit side by side. There `peek` shrinks
 *  the card to a full-width bar (logo + title + chevron) so the directions
 *  below get the height; tapping it (`onExpand`) shows the whole card again,
 *  capped to the top ~55% with its content scrolling inside it. */
export default function PoiPanel({
  venue,
  view,
  onOpenCategory,
  onSearch,
  onOpenPoi,
  onHome,
  onShowRoute,
  onShare,
  routeActive,
  compact = false,
  open = true,
  onOpen,
  onCollapse,
  collapsible = false,
  stacked = false,
  peek = false,
  onExpand,
  noAudio = false,
  headerExtras = null,
  docked = false,
  foldable = false,
  onFold,
}) {
  const { t, lang } = useT();

  if (docked) {
    return (
      <DockedPanel
        venue={venue}
        view={view}
        t={t}
        lang={lang}
        compact={compact}
        expanded={open && !peek}
        foldable={foldable}
        onUnfold={collapsible ? onOpen : onExpand}
        onFold={onFold}
        onOpenCategory={onOpenCategory}
        onSearch={onSearch}
        onOpenPoi={onOpenPoi}
        onHome={onHome}
        onShowRoute={onShowRoute}
        onShare={onShare}
        routeActive={routeActive}
        noAudio={noAudio}
        extras={headerExtras}
      />
    );
  }

  if (peek) {
    const title =
      view.type === "poi"
        ? pickName(view.poi?.name, lang)
        : view.type === "category"
          ? pickName(view.category?.name, lang)
          : view.type === "search"
            ? `“${view.q}”`
            : venue.name;
    return (
      <button
        onClick={onExpand}
        aria-label={t("open_menu")}
        aria-expanded="false"
        className="pointer-events-auto relative flex h-14 w-[340px] max-w-full shrink-0
                   items-center gap-3 rounded-2xl bg-[#26262c]/95 px-3 text-left text-white
                   shadow-2xl backdrop-blur active:bg-[#3a3a42]"
      >
        {venue.logo_path && (
          <img
            src={imageUrl(venue.logo_path)}
            alt=""
            className="h-9 w-9 shrink-0 rounded-lg bg-white object-contain p-0.5"
          />
        )}
        <span className="min-w-0 flex-1 truncate text-base font-bold">{title}</span>
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" className="shrink-0">
          <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.4"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    );
  }

  if (collapsible && !open) {
    return (
      <button
        onClick={onOpen}
        aria-label={t("open_menu")}
        className="absolute left-3 top-3 z-10 flex h-14 w-14 items-center justify-center
                   overflow-hidden rounded-2xl bg-[#26262c]/95 text-white shadow-2xl
                   backdrop-blur active:bg-[#3a3a42]"
      >
        {venue.logo_path ? (
          <img
            src={imageUrl(venue.logo_path)}
            alt={venue.name}
            className="h-full w-full bg-white object-contain p-1"
          />
        ) : (
          // A venue with no logo must not get nothing: same target, generic
          // glyph. Three bars reads as "menu" in every market we sell in.
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path
              d="M4 7h16M4 12h16M4 17h16"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        )}
      </button>
    );
  }

  return (
    <div
      className={
        "z-10 flex flex-col overflow-hidden bg-[#26262c]/95 text-white shadow-2xl " +
        "backdrop-blur " +
        (stacked
          ? // In the stacked column (ViewerPage): in flow, at most 55% of it,
            // content scrolls inside (PoiDetail/PoiList/PanelHome).
            "pointer-events-auto relative w-[340px] max-w-full max-h-[55%] shrink-0 rounded-2xl"
          : "absolute ") +
        (stacked ? "" : compact
          ? // Bottom sheet: full width, thumb-reachable, and the map stays
            // visible above it instead of behind it.
            // In portrait it also stops below the left control column
            // (zoom, compass, Reset: 257px), so Reset never sits on the
            // sheet's title on a 375x667 phone (QA on 1a14ce9). Landscape
            // keeps 62vh; there is no height to give up.
            "inset-x-2 bottom-2 max-h-[62vh] portrait:max-h-[min(62vh,calc(100vh-17rem))] rounded-2xl"
          : "left-3 top-3 max-h-[70vh] w-[340px] max-w-[calc(100vw-24px)] rounded-2xl")
      }
    >
      {/* Phone floating buttons (QR, assistant) live here while the sheet is
          open, next to the X, so nothing floats over the sheet (item 0324). */}
      {headerExtras && (
        <div
          className={
            "absolute top-2 z-10 flex items-center gap-1.5 " + (collapsible ? "right-12" : "right-2")
          }
          data-sheet-extras
        >
          {headerExtras}
        </div>
      )}
      {collapsible && (
        <button
          onClick={onCollapse}
          aria-label={t("hide_menu")}
          className="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center
                     rounded-full bg-black/30 text-white active:bg-black/50"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
      {view.type === "home" && (
        <PanelHome venue={venue} t={t} lang={lang} onOpenCategory={onOpenCategory} onSearch={onSearch} padRight={!!headerExtras} />
      )}
      {(view.type === "category" || view.type === "search") && (
        <PoiList
          venue={venue}
          view={view}
          t={t}
          lang={lang}
          onBack={onHome}
          onOpenPoi={onOpenPoi}
          onSearch={onSearch}
          padRight={!!headerExtras}
        />
      )}
      {view.type === "poi" && (
        <PoiDetail
          venue={venue}
          poi={view.poi}
          t={t}
          lang={lang}
          onBack={view.from ? () => view.from() : onHome}
          onShowRoute={onShowRoute}
          onShare={onShare}
          routeActive={routeActive}
          noAudio={noAudio}
          padRight={!!headerExtras}
        />
      )}
    </div>
  );
}

/** The menu docked at the TOP of a portrait or narrow screen (item 0373).
 *
 *  One header row that never changes shape -- logo, search field, fold chevron
 *  -- and, when expanded, the current view below it. Folded (a phone's resting
 *  state, or a totem following a route) it is that row alone: a compact search
 *  bar. Focusing the search or tapping the logo expands it downward. The input
 *  is the SAME element in both states, so a visitor who starts typing into the
 *  folded bar keeps the keyboard and the caret while the panel opens under it.
 *
 *  `extras` (the phone's assistant and QR buttons, item 0324/0325) sit at the
 *  right of the view's own title row rather than in the header, so the search
 *  field keeps the width it needs on a 375px phone.
 *
 *  Its height is read by ViewerPage, which pads the map camera by it: what is
 *  centred -- "You are here", the selected POI, a route -- is centred in the
 *  map left visible BELOW this panel, not behind it. */
function DockedPanel({
  venue,
  view,
  t,
  lang,
  compact,
  expanded,
  foldable,
  onUnfold,
  onFold,
  onOpenCategory,
  onSearch,
  onOpenPoi,
  onHome,
  onShowRoute,
  onShare,
  routeActive,
  noAudio,
  extras,
}) {
  const unfold = () => {
    if (!expanded) onUnfold?.();
  };
  return (
    <div
      data-dock-panel
      className={
        "pointer-events-auto relative flex min-h-0 shrink-0 flex-col overflow-hidden rounded-2xl " +
        "bg-[#26262c]/95 text-white shadow-2xl backdrop-blur " +
        // Phone: as tall as its content, up to the whole dock column (which
        // already stops above the bottom controls). With a route the
        // directions need the rest. A big portrait screen never gives the menu
        // more than half the column: the map is what a totem is for.
        (routeActive ? "max-h-[60%]" : compact ? "max-h-full" : "max-h-[50%]")
      }
    >
      <div className="flex items-center gap-2 p-2">
        <button
          onClick={expanded ? onHome : unfold}
          aria-label={expanded ? venue.name : t("open_menu")}
          className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl
                     bg-white active:opacity-80"
        >
          {venue.logo_path ? (
            <img src={imageUrl(venue.logo_path)} alt="" className="h-full w-full object-contain p-0.5" />
          ) : (
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" className="text-[#26262c]">
              <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          )}
        </button>
        <div className="min-w-0 flex-1">
          <SearchBox
            t={t}
            initial={view.type === "search" ? view.q : ""}
            clearWhen={view.type === "home"}
            onFocus={unfold}
            onSearch={(q) => {
              onSearch(q);
              unfold();
            }}
          />
        </div>
        {foldable && (
          <button
            onClick={expanded ? onFold : unfold}
            aria-label={expanded ? t("hide_menu") : t("open_menu")}
            aria-expanded={expanded}
            data-dock-fold
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-black/30
                       text-white active:bg-black/50"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path
                d={expanded ? "M6 15l6-6 6 6" : "M6 9l6 6 6-6"}
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
      {expanded && (
        <div className="flex min-h-0 flex-1 flex-col">
          {view.type === "home" && (
            <PanelHome
              venue={venue}
              t={t}
              lang={lang}
              onOpenCategory={onOpenCategory}
              onSearch={onSearch}
              docked
              extras={extras}
            />
          )}
          {(view.type === "category" || view.type === "search") && (
            <PoiList
              venue={venue}
              view={view}
              t={t}
              lang={lang}
              onBack={onHome}
              onOpenPoi={onOpenPoi}
              onSearch={onSearch}
              docked
              extras={extras}
            />
          )}
          {view.type === "poi" && (
            <PoiDetail
              venue={venue}
              poi={view.poi}
              t={t}
              lang={lang}
              onBack={view.from ? () => view.from() : onHome}
              onShowRoute={onShowRoute}
              onShare={onShare}
              routeActive={routeActive}
              noAudio={noAudio}
              extras={extras}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SearchBox({ t, initial = "", onSearch, autoFocus = false, onFocus, clearWhen = false }) {
  const [q, setQ] = useState(initial);
  useEffect(() => {
    const handle = setTimeout(() => {
      if (q.trim()) onSearch(q.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  // Docked header (item 0373): the field outlives the views, so "back" to the
  // home view has to empty it explicitly.
  useEffect(() => {
    if (clearWhen) setQ("");
  }, [clearWhen]);
  return (
    <div className="relative">
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
      >
        <circle cx="10.5" cy="10.5" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M15 15l5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <input
        value={q}
        autoFocus={autoFocus}
        onFocus={onFocus}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t("search_placeholder")}
        className="w-full rounded-lg bg-[#3a3a42] py-2.5 pl-9 pr-3 text-sm text-white
                   placeholder-slate-400 outline-none focus:ring-1 focus:ring-slate-400"
      />
    </div>
  );
}

function PanelHome({ venue, t, lang, onOpenCategory, onSearch, padRight = false, docked = false, extras = null }) {
  if (docked) {
    // The header row above already holds the logo and the search field; the
    // venue's name heads the grid. auto-fill: four columns on a phone, one or
    // two rows on a 1080px portrait totem instead of a 3-wide column of
    // oversized buttons.
    return (
      <>
        <div className="flex min-h-10 items-center gap-2 px-4 pb-1">
          <div className="min-w-0 flex-1 truncate text-base font-bold tracking-wide">{venue.name}</div>
          {extras && <div className="flex shrink-0 items-center gap-1.5" data-sheet-extras>{extras}</div>}
        </div>
        <div className="grid min-h-0 grid-cols-[repeat(auto-fill,minmax(84px,1fr))] gap-1 overflow-y-auto p-2 pt-0">
          {venue.categories.map((cat) => (
            <CategoryButton key={cat.id} cat={cat} lang={lang} onOpenCategory={onOpenCategory} />
          ))}
        </div>
      </>
    );
  }
  return (
    <>
      <div className="p-4 pb-2">
        <div className={"mb-3 flex items-center gap-3 " + (padRight ? "pr-36" : "")}>
          {venue.logo_path && (
            <img
              src={imageUrl(venue.logo_path)}
              alt=""
              className="h-11 w-11 shrink-0 rounded-lg bg-white object-contain p-0.5"
            />
          )}
          <div className="text-lg font-bold leading-tight tracking-wide">{venue.name}</div>
        </div>
        <SearchBox t={t} onSearch={onSearch} />
      </div>
      <div className="grid grid-cols-3 gap-1 overflow-y-auto p-3 pt-1">
        {venue.categories.map((cat) => (
          <CategoryButton key={cat.id} cat={cat} lang={lang} onOpenCategory={onOpenCategory} />
        ))}
      </div>
    </>
  );
}

function CategoryButton({ cat, lang, onOpenCategory }) {
  return (
    <button
      onClick={() => onOpenCategory(cat)}
      className="flex flex-col items-center gap-1.5 rounded-xl p-2.5 active:bg-white/10"
    >
      <span
        className="flex h-12 w-12 items-center justify-center rounded-full border-2
                   border-white bg-[#1b1b20] text-white"
      >
        <CategoryIcon icon={cat.icon} size={22} />
      </span>
      <span className="text-center text-[11px] leading-tight text-slate-200">
        {pickName(cat.name, lang)}
      </span>
    </button>
  );
}

function BackHeader({ title, onBack, t, padRight = false, extras = null }) {
  return (
    <div className={"flex items-center gap-2 p-3 pb-2 " + (padRight ? "pr-36" : "")}>
      <button
        onClick={onBack}
        aria-label={t("back")}
        className="flex h-9 w-9 items-center justify-center rounded-full active:bg-white/10"
      >
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path
            d="M14.5 5.5L8 12l6.5 6.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <div className="min-w-0 flex-1 truncate text-base font-semibold">{title}</div>
      {extras && <div className="flex shrink-0 items-center gap-1.5" data-sheet-extras>{extras}</div>}
    </div>
  );
}

function PoiList({ venue, view, t, lang, onBack, onOpenPoi, onSearch, padRight, docked = false, extras = null }) {
  const [pois, setPois] = useState(null);
  const floorsById = Object.fromEntries(venue.floors.map((f) => [f.id, f]));

  useEffect(() => {
    let cancelled = false;
    const params = view.type === "category" ? { category: view.category.slug } : { q: view.q };
    api
      .pois(venue.slug, params)
      .then((rows) => !cancelled && setPois(rows))
      .catch(() => !cancelled && setPois([]));
    return () => {
      cancelled = true;
    };
  }, [venue.slug, view.type === "category" ? view.category.slug : view.q]); // eslint-disable-line react-hooks/exhaustive-deps

  const title =
    view.type === "category" ? pickName(view.category.name, lang) : `“${view.q}”`;

  return (
    <>
      <BackHeader title={title} onBack={onBack} t={t} padRight={padRight} extras={extras} />
      {/* Docked (item 0373): the header's own field is the search box. */}
      {view.type === "search" && !docked && (
        <div className="px-3 pb-2">
          <SearchBox t={t} initial={view.q} onSearch={onSearch} autoFocus />
        </div>
      )}
      <div className="min-h-0 overflow-y-auto px-2 pb-2" data-poi-list>
        {pois === null && (
          <div className="p-4 text-sm text-slate-400">{t("loading")}</div>
        )}
        {pois?.length === 0 && (
          <div className="p-4 text-sm text-slate-400">{t("no_results")}</div>
        )}
        {pois?.map((poi) => (
          <button
            key={poi.id}
            onClick={() => onOpenPoi(poi.slug)}
            className="flex w-full items-center gap-3 rounded-xl p-2.5 text-left active:bg-white/10"
          >
            {poi.logo_path ? (
              <img
                src={imageUrl(poi.logo_path)}
                alt=""
                className="h-10 w-10 shrink-0 rounded-full bg-white object-cover"
              />
            ) : (
              <span className="h-10 w-10 shrink-0 rounded-full bg-slate-600" />
            )}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">
                {pickName(poi.name, lang)}
              </span>
              <span className="text-xs text-slate-400">
                {floorsById[poi.floor_id]?.short_label ?? ""}
              </span>
            </span>
            {poi.favorite && <span className="text-yellow-400">★</span>}
          </button>
        ))}
      </div>
    </>
  );
}

function PoiDetail({ venue, poi, t, lang, onBack, onShowRoute, onShare, routeActive, noAudio, padRight, extras = null }) {
  const floorsById = Object.fromEntries(venue.floors.map((f) => [f.id, f]));
  const description = pickName(poi.description, lang)?.trim();

  return (
    <>
      <BackHeader title={pickName(poi.name, lang)} onBack={onBack} t={t} padRight={padRight} extras={extras} />
      {/* Scrolls inside the card when the card is capped (the 641-819px band
          with a route, item 0314), so "Show route" and "Share" are reachable
          however tall the photo and description are. */}
      <div className="min-h-0 overflow-y-auto">
      {poi.video_path ? (
        // The clip takes the photo's place; its poster (or the photo) shows
        // while the player chunk loads. Keyed on the POI so opening another
        // one tears the old player down instead of reusing its buffer.
        <Suspense
          fallback={
            (poi.video_poster_path || poi.photo_path) && (
              <img
                src={imageUrl(poi.video_poster_path || poi.photo_path)}
                alt=""
                className="max-h-56 w-full bg-black object-contain"
              />
            )
          }
        >
          <PoiVideo key={poi.id} poi={poi} lowPower={lowPower().on} noAudio={noAudio} t={t} />
        </Suspense>
      ) : (
        poi.photo_path && (
          <img src={imageUrl(poi.photo_path)} alt="" className="max-h-40 w-full object-cover" />
        )
      )}
      <div className="flex items-center gap-3 p-4 pt-3">
        {poi.logo_path && (
          <img
            src={imageUrl(poi.logo_path)}
            alt=""
            className="h-14 w-14 rounded-full bg-white object-cover"
          />
        )}
        <div>
          <div className="text-base font-semibold">{pickName(poi.name, lang)}</div>
          <div className="text-sm text-slate-400">
            {t("floor_label")} {floorsById[poi.floor_id]?.short_label ?? ""}
          </div>
        </div>
      </div>
      {/* The description is a free-text field the venue admin types in a plain
          textarea, so it carries real newlines (item 0265: "it dont respect the
          Intros"). `whitespace-pre-wrap` keeps them -- blank lines between
          paragraphs included -- while still wrapping normally; `break-words`
          stops a pasted URL, which has no space to break at, from widening the
          panel past its own box.

          `{description}` stays a JSX child on purpose. The stored value is raw,
          unsanitised text and ANY tenant admin can write it for their own venue
          (require_admin + venue scope, not staff-only), so React's escaping of
          this one expression is the whole XSS defence for the field. Never
          dangerouslySetInnerHTML here, and never assemble markup to linkify --
          if URLs are ever to be live, they must be real <a> elements returned
          from JSX. See the item for why they are deliberately not live yet. */}
      {description && (
        <p className="px-4 pb-2 text-sm whitespace-pre-wrap break-words text-slate-300">
          {description}
        </p>
      )}
      {(poi.opening_hours || poi.phone) && (
        <div className="px-4 pb-2 text-xs text-slate-400">
          {poi.opening_hours && <div>{poi.opening_hours}</div>}
          {poi.phone && <div>{poi.phone}</div>}
        </div>
      )}
      <div className="flex gap-2 p-4 pt-2">
        {onShowRoute && (
          <button
            onClick={() => onShowRoute(poi)}
            className="flex-1 rounded-xl bg-[#e63946] py-3 text-sm font-bold text-white
                       shadow-md active:bg-[#c92e3b]"
          >
            {/* Deliberately NOT disabled while a route is active (item 0091).
                It used to be, so a visitor looking at a route who picked another
                shop found the only useful button greyed out and had to find
                "Cancelar" in the opposite corner first. Picking a destination
                re-routes -- onShowRoute replaces the route, the floor follows
                the new first segment -- which is what every map they have ever
                used does. */}
            {routeActive ? t("show_route_again") : t("show_route")}
          </button>
        )}
        {onShare && (
          <button
            onClick={() => onShare(poi)}
            className="rounded-xl bg-white/10 px-4 py-3 text-sm font-semibold text-white
                       active:bg-white/20"
          >
            {t("share")}
          </button>
        )}
      </div>
      </div>
    </>
  );
}
